import { api, type Organization, type OrgSettings } from "../api/client";
import { allTags, loadItems, type DecryptedItem } from "../vault/items";
import { ShareDialog } from "./ShareDialog";
import { VersionHistory } from "./VersionHistory";
import { ImportDialog } from "./ImportDialog";
import { ExportDialog } from "./ExportDialog";
import { SecurityHealthPanel } from "./SecurityHealthPanel";
import { BulkEditDialog, type BulkEdit } from "./BulkEditDialog";
import { ItemEditor, type EditorState } from "./ItemEditor";
import { NewNodeDialog } from "./NewNodeDialog";
import { MoveDialog } from "./MoveDialog";
import { VaultSettingsDialog } from "./VaultSettingsDialog";
import { RecoveryKeyModal } from "./RecoveryKeyModal";
import { type FolderNode } from "./folder-tree";
import { useToast } from "./toast-context";
import { useT } from "../i18n";

type DialogHostProps = {
  selectedOrg: Organization | null;
  orgKeyFor: (org: Organization) => Uint8Array;
  orgs: Organization[];
  items: DecryptedItem[];
  folders: FolderNode[];
  selectedIds: Set<string>;
  selectedFolderId: string | null;
  orgSettings: OrgSettings | null;
  setOrgSettings: (s: OrgSettings) => void;
  reloadItems: () => Promise<void>;
  setSelectedId: (id: string) => void;
  sharing: string | null;
  setSharing: (v: string | null) => void;
  historyId: string | null;
  setHistoryId: (v: string | null) => void;
  importing: boolean;
  setImporting: (v: boolean) => void;
  exporting: boolean;
  setExporting: (v: boolean) => void;
  newNode: { kind: "folder" | "device"; parentId: string | null } | null;
  setNewNode: (n: { kind: "folder" | "device"; parentId: string | null } | null) => void;
  submitNewNode: (name: string, category?: string, address?: string) => Promise<void>;
  editNode: { id: string; kind: "folder" | "device"; name: string; category?: string; address?: string } | null;
  setEditNode: (n: { id: string; kind: "folder" | "device"; name: string; category?: string; address?: string } | null) => void;
  submitEditNode: (name: string, category?: string, address?: string) => Promise<void>;
  movingIds: Set<string> | null;
  setMovingIds: (ids: Set<string> | null) => void;
  settingsOpen: boolean;
  setSettingsOpen: (v: boolean) => void;
  recoveryKeyShown: string | null;
  setRecoveryKeyShown: (v: string | null) => void;
  healthOpen: boolean;
  setHealthOpen: (v: boolean) => void;
  bulkEditing: boolean;
  setBulkEditing: (v: boolean) => void;
  applyBulkEdit: (e: BulkEdit) => Promise<void>;
  editor: EditorState | null;
  setEditor: (e: EditorState | null) => void;
};

/** All vault-scoped modals (share, history, import/export, node dialogs, move, vault settings,
 *  recovery key, health scan, bulk edit, item editor), in their original mount order. */
export function DialogHost({
  selectedOrg,
  orgKeyFor,
  orgs,
  items,
  folders,
  selectedIds,
  selectedFolderId,
  orgSettings,
  setOrgSettings,
  reloadItems,
  setSelectedId,
  sharing,
  setSharing,
  historyId,
  setHistoryId,
  importing,
  setImporting,
  exporting,
  setExporting,
  newNode,
  setNewNode,
  submitNewNode,
  editNode,
  setEditNode,
  submitEditNode,
  movingIds,
  setMovingIds,
  settingsOpen,
  setSettingsOpen,
  recoveryKeyShown,
  setRecoveryKeyShown,
  healthOpen,
  setHealthOpen,
  bulkEditing,
  setBulkEditing,
  applyBulkEdit,
  editor,
  setEditor,
}: DialogHostProps) {
  const t = useT();
  const toast = useToast();
  return (
    <>
      {sharing && selectedOrg && (
        <ShareDialog itemId={sharing} orgKey={orgKeyFor(selectedOrg)} onClose={() => setSharing(null)} />
      )}

      {historyId && selectedOrg && (
        <VersionHistory
          itemId={historyId}
          orgKey={orgKeyFor(selectedOrg)}
          onClose={() => setHistoryId(null)}
          onRestored={async () => {
            setHistoryId(null);
            await reloadItems();
          }}
        />
      )}

      {importing && selectedOrg && (
        <ImportDialog
          orgId={selectedOrg.id}
          orgKey={orgKeyFor(selectedOrg)}
          onClose={() => setImporting(false)}
          onDone={async (result) => {
            setImporting(false);
            await reloadItems();
            if (result && result.failed > 0) {
              const detail = result.firstError ? ` — ${result.firstError}` : "";
              toast.error(`${t("Imported")} ${result.imported}, ${result.failed} ${t("failed")}${detail}`);
            } else {
              toast.success(t("Import complete"));
            }
          }}
        />
      )}

      {exporting && selectedOrg && (
        <ExportDialog orgId={selectedOrg.id} orgName={selectedOrg.name} items={items} onClose={() => setExporting(false)} />
      )}

      {newNode && (
        <NewNodeDialog kind={newNode.kind} onClose={() => setNewNode(null)} onCreate={submitNewNode} />
      )}

      {editNode && (
        <NewNodeDialog
          kind={editNode.kind}
          initial={{ name: editNode.name, category: editNode.category, address: editNode.address }}
          submitLabel={t("Save")}
          onClose={() => setEditNode(null)}
          onCreate={submitEditNode}
        />
      )}

      {movingIds && selectedOrg && (
        <MoveDialog
          count={movingIds.size}
          folders={folders}
          onClose={() => setMovingIds(null)}
          onMove={async (folderId) => {
            const ids = [...movingIds];
            setMovingIds(null);
            for (const id of ids) {
              try {
                await api.moveItem(id, folderId || undefined);
              } catch {
                /* skip */
              }
            }
            await reloadItems();
          }}
        />
      )}

      {settingsOpen && selectedOrg && (
        <VaultSettingsDialog
          orgId={selectedOrg.id}
          orgName={selectedOrg.is_personal ? t("Personal vault") : selectedOrg.name}
          initial={orgSettings ?? { default_offline_allowed: false }}
          onClose={() => setSettingsOpen(false)}
          onSaved={(s) => {
            setOrgSettings(s);
            setSettingsOpen(false);
          }}
        />
      )}

      {recoveryKeyShown && <RecoveryKeyModal recoveryKey={recoveryKeyShown} onClose={() => setRecoveryKeyShown(null)} />}

      {healthOpen && (
        <SecurityHealthPanel
          items={items}
          scanAll={async () => {
            const all: DecryptedItem[] = [];
            for (const o of orgs) {
              try {
                all.push(...(await loadItems(o.id, orgKeyFor(o))));
              } catch {
                /* skip vaults that fail to load */
              }
            }
            return all;
          }}
          onSelect={(id) => setSelectedId(id)}
          onClose={() => setHealthOpen(false)}
        />
      )}

      {bulkEditing && selectedOrg && (
        <BulkEditDialog
          count={selectedIds.size}
          folders={folders}
          onClose={() => setBulkEditing(false)}
          onApply={(e) => void applyBulkEdit(e)}
        />
      )}

      {/* Navigating to another vault closes the editor explicitly (useVaultWorkspace.openOrg /
          openShared) instead of leaving this condition to unmount it behind a still-set `editor`. */}
      {editor && selectedOrg && (
        <ItemEditor
          state={editor}
          orgKey={orgKeyFor(selectedOrg)}
          orgId={selectedOrg.id}
          folderId={selectedFolderId}
          folders={folders}
          knownTags={allTags(items)}
          defaultOfflineAllowed={orgSettings?.default_offline_allowed ?? true}
          onClose={() => setEditor(null)}
          onSaved={async () => {
            setEditor(null);
            await reloadItems();
            toast.success(t("Saved"));
          }}
        />
      )}
    </>
  );
}
